import React, { useEffect, useState } from "react";
import { api, formatErr, fmtDateTime, money } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, EmptyState } from "@/components/shared";
import { Plus, Trash2, ArrowDownToLine } from "lucide-react";
import { toast } from "sonner";

const METHODS = ["Cash", "Zelle", "Cash App", "Card", "Other"];
const CATEGORIES = [
  { v: "deposit", l: "Deposit" },
  { v: "rental", l: "Rental Payment" },
  { v: "late_fee", l: "Late Fee" },
  { v: "toll", l: "Toll" },
  { v: "damage", l: "Damage Fee" },
  { v: "other", l: "Other" },
];

export default function Payments() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ reservation_id: "", customer_id: "", amount: 0, method: "Cash", category: "rental", notes: "" });

  const load = async () => {
    const [p, c, r] = await Promise.all([api.get("/payments"), api.get("/customers"), api.get("/reservations")]);
    setRows(p.data);
    setCustomers(c.data);
    setReservations(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    try {
      if (!form.customer_id || !form.amount) return toast.error("Customer and amount required");
      const payload = { ...form, amount: Number(form.amount), reservation_id: form.reservation_id || null };
      await api.post("/payments", payload);
      toast.success("Payment recorded");
      setOpen(false);
      setForm({ reservation_id: "", customer_id: "", amount: 0, method: "Cash", category: "rental", notes: "" });
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this payment?")) return;
    try {
      await api.delete(`/payments/${id}`);
      toast.success("Payment deleted");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const customerReservations = reservations.filter((r) => r.customer_id === form.customer_id);

  const totalIn = rows.reduce((s, p) => s + Number(p.amount || 0), 0);
  const totalDue = reservations.reduce((s, r) => s + (r.status !== "Cancelled" ? Number(r.balance_due || 0) : 0), 0);

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <PageHeader
        title="Payments"
        subtitle="Cash flow & balances"
        testId="payments-page-header"
        action={
          <Button data-testid="add-payment-button" onClick={() => setOpen(true)} className="bg-black hover:bg-gray-800 text-white">
            <Plus size={16} className="mr-2" /> Record Payment
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
        <div className="kpi-card">
          <div className="overline">Total Collected</div>
          <div className="font-heading text-3xl font-bold mt-2 text-green-700 tabular-nums">{money(totalIn)}</div>
        </div>
        <div className="kpi-card">
          <div className="overline">Weekly Outstanding</div>
          <div className="font-heading text-3xl font-bold mt-2 text-amber-700 tabular-nums">{money(totalDue)}</div>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState title="No payments yet" testId="payments-empty" action={<Button onClick={() => setOpen(true)}>Record first payment</Button>} />
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="payments-list">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Customer</th>
                  <th className="text-left px-4 py-3">Category</th>
                  <th className="text-left px-4 py-3">Method</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">Notes</th>
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50" data-testid={`payment-row-${p.id}`}>
                    <td className="px-4 py-3">{fmtDateTime(p.paid_at)}</td>
                    <td className="px-4 py-3 font-medium">{p.customer_name}</td>
                    <td className="px-4 py-3 capitalize">{p.category?.replace("_", " ")}</td>
                    <td className="px-4 py-3">{p.method}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-green-700">{money(p.amount)}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-[300px] truncate">{p.notes}</td>
                    <td className="px-2 py-2 text-right">
                      {isAdmin && (
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => del(p.id)} data-testid={`delete-payment-${p.id}`}>
                          <Trash2 size={13} />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <ArrowDownToLine size={18} />
              Record Payment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Customer</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v, reservation_id: "" })}>
                <SelectTrigger data-testid="pay-customer-select"><SelectValue placeholder="Choose customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Linked reservation (optional)</Label>
              <Select value={form.reservation_id || "none"} onValueChange={(v) => setForm({ ...form, reservation_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="pay-res-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {customerReservations.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.vehicle_label} · {fmtDateTime(r.pickup_datetime)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount</Label>
                <Input type="number" step="0.01" data-testid="pay-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </div>
              <div>
                <Label>Method</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="pay-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea rows={2} data-testid="pay-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="pay-cancel">Cancel</Button>
            <Button onClick={submit} className="bg-black hover:bg-gray-800 text-white" data-testid="pay-save">Save Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
